import React, { useEffect, useRef } from "react";
import { projects } from "../data/site";
import { ArrowRight } from "./Icons";
import Reveal from "./Reveal";
import SectionLabel from "./SectionLabel";
import Clients from "./Clients";
import useMediaQuery from "../hooks/useMediaQuery";
import { cx, display, inner } from "../lib/ui";

function ProjectCard({ project, i, fixedWidth }) {
  return (
    <article className={cx("group", fixedWidth && "w-[clamp(250px,25vw,390px)] flex-none")}>
      <div className="relative aspect-[3/4] overflow-hidden bg-paper-50">
        <img
          src={project.image}
          alt={project.name}
          loading="lazy"
          className="h-full w-full scale-[1.03] object-cover grayscale contrast-[1.06] transition-[transform,filter] duration-[1200ms] ease-out group-hover:scale-[1.09] group-hover:contrast-100 group-hover:grayscale-0"
        />
        <span className="absolute left-0 top-0 z-10 bg-ink px-4 py-2.5 text-[11px] font-semibold tracking-[0.2em] text-paper">
          {String(i + 1).padStart(2, "0")}
        </span>
      </div>

      <div className="grid gap-2 pt-[18px]">
        <h3 className={cx(display, "text-[clamp(20px,1.9vw,27px)]")}>{project.name}</h3>
        <p className="flex flex-wrap items-center gap-2.5 text-[11px] uppercase tracking-[0.14em] text-ash-400">
          <span>{project.category}</span>
          <i className="block h-[3px] w-[3px] rounded-full bg-ash-300" />
          <span>{project.location}</span>
          <i className="block h-[3px] w-[3px] rounded-full bg-ash-300" />
          <span>{project.year}</span>
        </p>
      </div>
    </article>
  );
}

export default function Projects() {
  const isDesktop = useMediaQuery("(min-width: 980px)");
  const wrapRef = useRef(null);
  const trackRef = useRef(null);

  /* The gallery is driven by page scroll: a tall spacer holds a sticky
     viewport, and vertical distance becomes horizontal travel. The
     transform is written straight to the node — no re-render per frame. */
  useEffect(() => {
    if (!isDesktop) return undefined;

    let frame = 0;

    const measure = () => {
      frame = 0;
      const wrap = wrapRef.current;
      const track = trackRef.current;
      if (!wrap || !track) return;

      const total = wrap.offsetHeight - window.innerHeight;
      const scrolled = Math.min(
        Math.max(-wrap.getBoundingClientRect().top, 0),
        Math.max(total, 0)
      );
      const p = total > 0 ? scrolled / total : 0;
      const distance = Math.max(track.scrollWidth - window.innerWidth, 0);

      track.style.transform = `translate3d(${-(p * distance).toFixed(2)}px, 0, 0)`;
    };

    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [isDesktop]);

  return (
    <section id="projects" className="relative scroll-mt-nav bg-paper pt-section text-ink">
      <div className={cx(inner, "px-gutter")}>
        <header className="grid gap-[clamp(22px,3vw,44px)] border-b border-black/10 pb-[clamp(38px,5vw,66px)] lg:grid-cols-[200px_1fr] lg:items-start lg:gap-x-[clamp(24px,4vw,60px)]">
          <SectionLabel>Projects</SectionLabel>

          <h2 className={cx(display, "text-[clamp(38px,6.6vw,92px)] lg:col-start-2")}>
            <Reveal as="span" mask>
              Selected
            </Reveal>
            <Reveal as="span" mask delay={90} className="italic pl-[clamp(0px,6vw,110px)]">
              work &amp; clients
            </Reveal>
          </h2>

          <Reveal
            className="grid max-w-[52ch] gap-3.5 text-[15px] leading-[1.7] text-ink-500 lg:col-start-2"
            delay={160}
          >
            <p>
              We deal with dream projects at exclusive locations — commercial,
              residential, land and weekend property across Ahmedabad.
            </p>
            {isDesktop && (
              <span className="inline-flex items-center gap-2.5 text-[11px] uppercase tracking-[0.18em] text-ash-400">
                <ArrowRight width="15" height="15" className="animate-nudge" />
                Keep scrolling — the gallery moves sideways
              </span>
            )}
          </Reveal>
        </header>
      </div>

      {isDesktop ? (
        <div ref={wrapRef} className="relative" style={{ height: `${100 + projects.length * 52}vh` }}>
          <div className="sticky top-0 flex h-[100svh] flex-col justify-center overflow-hidden">
            <div
              ref={trackRef}
              className="flex w-max items-start gap-[clamp(20px,2.6vw,46px)] px-gutter will-change-transform"
            >
              {projects.map((project, i) => (
                <ProjectCard key={project.id} project={project} i={i} fixedWidth />
              ))}

              <div className="grid w-[clamp(220px,20vw,300px)] flex-none content-center gap-2.5 self-stretch border-l border-black/10 pl-[clamp(20px,3vw,48px)]">
                <p className="font-sans text-[11px] font-medium uppercase tracking-[0.24em] text-ash-400">
                  Next
                </p>
                <p className={cx(display, "text-[clamp(26px,2.6vw,40px)] italic text-ash-300")}>
                  Your project here
                </p>
              </div>
            </div>

          </div>
        </div>
      ) : (
        <div className="px-gutter">
          <div className={cx(inner, "grid gap-10 pt-10 sm:grid-cols-2 sm:gap-8")}>
            {projects.map((project, i) => (
              <ProjectCard key={project.id} project={project} i={i} />
            ))}
          </div>
        </div>
      )}

      <Clients />
    </section>
  );
}
